import type { ConversationFacts } from './conversations-contracts'
import type {
  CallTimelineFacts,
  InteractionCall,
} from './interaction-calls-contracts'

/**
 * One phone conversation: a call, and the Ava call the caller came back to when
 * the front desk didn't answer it. Calls join only by the owner's exact record
 * that one continues the other, never because they share a phone number.
 */
export type CallGroup = {
  key: string
  /** Oldest first. */
  legs: InteractionCall[]
  /** The first leg continues a call that isn't loaded yet (an older page). */
  earlierCallNotLoaded: boolean
}

export function groupCalls(calls: InteractionCall[]): CallGroup[] {
  const byRef = new Map(calls.map((call) => [call.callRef, call]))
  const rootOf = (call: InteractionCall) => {
    const seen = new Set([call.callRef])
    let current = call
    for (;;) {
      const parentRef = current.timeline?.returnOfCallRef
      const parent = parentRef ? byRef.get(parentRef) : undefined
      if (!parent || seen.has(parent.callRef)) return current
      seen.add(parent.callRef)
      current = parent
    }
  }
  const groups = new Map<string, InteractionCall[]>()
  for (const call of calls) {
    const key = rootOf(call).callRef
    groups.set(key, [...(groups.get(key) ?? []), call])
  }
  return [...groups.entries()].map(([key, legs]) => {
    const ordered = [...legs].sort(
      (left, right) =>
        left.startedAtMs - right.startedAtMs ||
        left.callRef.localeCompare(right.callRef)
    )
    return {
      key,
      legs: ordered,
      earlierCallNotLoaded: Boolean(ordered[0]?.timeline?.returnOfCallRef),
    }
  })
}

/** Whether the group has a handoff to tell, so it reads as a timeline. */
export function hasTimeline(group: CallGroup) {
  return (
    group.legs.length > 1 ||
    group.earlierCallNotLoaded ||
    group.legs.some(
      (call) =>
        call.timeline?.state !== undefined ||
        call.timeline?.callback !== undefined ||
        call.timeline?.information !== undefined ||
        call.timeline?.requestPassed !== undefined
    )
  )
}

/**
 * The group's facts as they stand at its end: the latest leg speaks for the
 * transfer and the callback, and its intent already reads over both legs. A
 * returned call the caller said nothing on has no intent of its own.
 */
export function finalFacts(group: CallGroup): CallTimelineFacts | undefined {
  const latest = group.legs.at(-1)?.timeline
  const reason = [...group.legs]
    .reverse()
    .find((call) => call.timeline?.intent !== undefined)?.timeline
  if (!latest) return reason
  return {
    ...latest,
    intent: latest.intent ?? reason?.intent,
    secondary: latest.secondary ?? reason?.secondary,
    topics: latest.topics ?? reason?.topics,
    information: latest.information ?? reason?.information,
    requestPassed: latest.requestPassed ?? reason?.requestPassed,
    need: latest.need ?? reason?.need,
  }
}

/** The shape every presentation reads, from a call's own facts. */
export function conversationFacts(
  timeline: CallTimelineFacts | undefined
): ConversationFacts {
  if (!timeline) return {}
  const { state, reason, callback } = timeline
  return {
    intent: timeline.intent,
    secondary: timeline.secondary,
    handoff: { state, reason, callback },
    information: timeline.information,
    requestPassed: timeline.requestPassed,
    need: timeline.need,
    topics: timeline.topics,
  }
}

export function totalDurationSeconds(group: CallGroup) {
  return group.legs.reduce((total, call) => total + call.durationSeconds, 0)
}
